import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Image,
  Mic,
  MessageSquare,
  Clock,
  CheckCircle,
  Copy,
  Check,
  Upload,
  X,
  FileText,
  Building2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getWhatsAppSalesUrl } from '@/utils/contactLinks'
import {
  fetchBillingSummary,
  fetchAvailablePlans,
  selectPlan,
  fetchPagoMovilInfo,
  fetchMyPaymentReports,
  submitPaymentReport,
  type PlanItem,
  type PaymentReportItem,
} from '@/services/billingApi'

export default function BillingPage() {
  const queryClient = useQueryClient()
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null)

  // Estado del Modal de Reporte de Pago Móvil
  const [reportingPlan, setReportingPlan] = useState<PlanItem | null>(null)
  const [referenceNumber, setReferenceNumber] = useState('')
  const [issuingBank, setIssuingBank] = useState('')
  const [notes, setNotes] = useState('')
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const { data: summary, isLoading: loadingSummary, error: summaryError } = useQuery({
    queryKey: ['billingSummary'],
    queryFn: fetchBillingSummary,
    refetchInterval: 15000,
  })

  const { data: plans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['availablePlans'],
    queryFn: fetchAvailablePlans,
  })

  const { data: pagoMovilInfo } = useQuery({
    queryKey: ['pagoMovilInfo'],
    queryFn: fetchPagoMovilInfo,
    staleTime: 60000,
  })

  const { data: myReports = [] } = useQuery({
    queryKey: ['myPaymentReports'],
    queryFn: fetchMyPaymentReports,
  })

  const changePlanMutation = useMutation({
    mutationFn: selectPlan,
    onSuccess: (data) => {
      toast.success(data.message || 'Plan actualizado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      setSelectedPlanCode(null)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Error al cambiar de plan')
    },
  })

  const submitReportMutation = useMutation({
    mutationFn: submitPaymentReport,
    onSuccess: (data) => {
      toast.success(data.message || 'Reporte de pago enviado con éxito')
      queryClient.invalidateQueries({ queryKey: ['myPaymentReports'] })
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      closePaymentModal()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err.message || 'Error al registrar el reporte de pago')
    },
  })

  const handleSelectPlan = (code: string) => {
    if (confirm(`¿Deseas activar el plan ${code.toUpperCase()} para tu cuenta?`)) {
      setSelectedPlanCode(code)
      changePlanMutation.mutate(code)
    }
  }

  const openPaymentModal = (plan: PlanItem) => {
    setReportingPlan(plan)
    setReferenceNumber('')
    setIssuingBank('')
    setNotes('')
    setReceiptFile(null)
    setReceiptPreview(null)
  }

  const closePaymentModal = () => {
    setReportingPlan(null)
    setReceiptFile(null)
    setReceiptPreview(null)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('La imagen no debe superar los 10MB')
        return
      }
      setReceiptFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setReceiptPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(fieldName)
    toast.success(`${fieldName} copiado al portapapeles`)
    setTimeout(() => setCopiedField(null), 2500)
  }

  const handleSubmitReport = (e: React.FormEvent) => {
    e.preventDefault()
    if (!reportingPlan) return
    if (!referenceNumber.trim()) {
      toast.error('Ingresa el número de referencia del pago')
      return
    }
    if (!receiptFile) {
      toast.error('Adjunta la captura del comprobante de pago')
      return
    }

    const formData = new FormData()
    formData.append('planId', reportingPlan.id)
    formData.append('referenceNumber', referenceNumber.trim())
    if (issuingBank.trim()) formData.append('issuingBank', issuingBank.trim())
    if (notes.trim()) formData.append('notes', notes.trim())
    formData.append('receipt', receiptFile)

    submitReportMutation.mutate(formData)
  }

  if (loadingSummary || loadingPlans) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#075f55] border-t-transparent" />
      </div>
    )
  }

  if (summaryError || !summary) {
    return (
      <div className="p-6 text-center text-red-600 bg-red-50 rounded-xl border border-red-200">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="font-semibold">No se pudo cargar la información de facturación</p>
        <p className="text-xs text-red-400 mt-1">Verifica tu conexión o contacta a soporte.</p>
      </div>
    )
  }

  const { credits, isTrial, daysRemaining, status, planName, planCode } = summary
  const isDepleted = credits.remaining <= 0
  const isExpired = status === 'expired' || (isTrial && daysRemaining !== null && daysRemaining <= 0)
  const isLowCredits = credits.percentageUsed >= 80

  // Cálculo del monto en Bs a tasa BCV si hay un plan en reporte
  const bcvRate = pagoMovilInfo?.bcvRate || 68.5
  const reportingAmountUsd = reportingPlan ? reportingPlan.priceCents / 100 : 0
  const reportingAmountBs = Math.round(reportingAmountUsd * bcvRate * 100) / 100

  return (
    <div className="flex-1 overflow-y-auto pb-12 max-w-6xl mx-auto px-4 sm:px-6">

      {/* ── ALERTA DE VENCIMIENTO O CRÉDITOS AGOTADOS ── */}
      {isExpired && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-bold text-sm">Periodo de prueba finalizado</h3>
            <p className="text-red-700 text-xs mt-0.5">
              Tus 7 días de prueba han expirado. Las respuestas automáticas del bot están en pausa y transferidas a atención manual.
              Selecciona uno de los planes a continuación y reporta tu pago para reactivarlo al instante.
            </p>
          </div>
        </div>
      )}

      {!isExpired && isDepleted && (
        <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-amber-800 font-bold text-sm">Créditos de IA agotados (0 restantes)</h3>
            <p className="text-amber-700 text-xs mt-0.5">
              Has alcanzado el límite de créditos de tu ciclo. Las conversaciones se están transfiriendo a tus asesores humanos.
              Actualiza a un plan superior para recargar tus créditos de inmediato.
            </p>
          </div>
        </div>
      )}

      {!isExpired && !isDepleted && isLowCredits && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3">
          <Zap className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-blue-800 font-bold text-sm">Aviso de consumo: Te queda menos del 20% de créditos</h3>
            <p className="text-blue-700 text-xs mt-0.5">
              Has consumido el {credits.percentageUsed}% de tus créditos ({credits.remaining} restantes).
              Considera actualizar tu plan para no pausar la atención en momentos de alta demanda.
            </p>
          </div>
        </div>
      )}

      {/* ── PLAN ACTUAL Y ESTADO DE CRÉDITOS ── */}
      <section className="pt-2">
        <h2 data-tour="billing-main" className="text-slate-900 text-xs font-bold uppercase tracking-wider mb-3 text-slate-500">
          Suscripción y Saldo de Inteligencia Artificial
        </h2>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-black text-slate-900">{planName}</span>
                {isTrial && (
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" /> PRUEBA GRATUITA (7 DÍAS)
                  </span>
                )}
                {!isTrial && status === 'active' && (
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> PLAN ACTIVO
                  </span>
                )}
                {isExpired && (
                  <span className="bg-red-100 text-red-800 text-xs font-bold px-3 py-1 rounded-full">
                    VENCIDO
                  </span>
                )}
              </div>
              <p className="text-slate-500 text-xs mt-1">
                {isTrial
                  ? `Periodo de prueba para validar ventas y respuestas de catálogo con clientes reales.`
                  : `Plan comercial recurrente con facturación mensual.`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {isTrial && daysRemaining !== null && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#075f55]" />
                  <div className="text-left">
                    <p className="text-[10px] uppercase font-bold text-slate-400 leading-none">Días de prueba</p>
                    <p className="text-sm font-black text-slate-800 leading-tight">
                      {daysRemaining > 0 ? `${daysRemaining} días restantes` : 'Finalizado'}
                    </p>
                  </div>
                </div>
              )}
              <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-left">
                <p className="text-[10px] uppercase font-bold text-slate-400 leading-none">Precio mensual</p>
                <p className="text-sm font-black text-slate-800 leading-tight">{summary.priceFormatted}</p>
              </div>
            </div>
          </div>

          {/* BARRA DE PROGRESO DE CRÉDITOS */}
          <div className="pt-6">
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-[#075f55]" />
                Saldo de Créditos IA
              </span>
              <span className="text-xs font-semibold text-slate-600">
                <strong className="text-base text-slate-900">{credits.remaining.toLocaleString()}</strong> de {credits.total.toLocaleString()} créditos disponibles ({100 - credits.percentageUsed}% libre · {summary.breakdown.totalMessagesCount || credits.used} mensajes procesados)
              </span>
            </div>

            {/* Barra */}
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 border border-slate-200">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isDepleted
                    ? 'bg-red-500'
                    : isLowCredits
                    ? 'bg-amber-500'
                    : 'bg-[#075f55]'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, credits.percentageUsed))}%` }}
              />
            </div>

            {/* TASA DE CONSUMO EXPLICADA */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 pt-5 border-t border-slate-100 text-xs text-slate-600">
              <div className="flex items-center gap-3 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">1 crédito / texto</p>
                  <p className="text-[11px] text-slate-500">
                    {summary.breakdown.textMessagesCount || summary.breakdown.textCreditsUsed} textos ({summary.breakdown.textCreditsUsed} créditos)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-800 flex items-center justify-center font-black">
                  <Image className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">3 créditos / foto</p>
                  <p className="text-[11px] text-slate-500">
                    {summary.breakdown.imageMessagesCount || Math.round(summary.breakdown.imageCreditsUsed / 3)} fotos ({summary.breakdown.imageCreditsUsed} créditos)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-black">
                  <Mic className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">2 créditos / audio</p>
                  <p className="text-[11px] text-slate-500">
                    {summary.breakdown.audioMessagesCount || Math.round(summary.breakdown.audioCreditsUsed / 2)} audios ({summary.breakdown.audioCreditsUsed} créditos)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPARADOR DE PLANES Y BOTONES DE PAGO ── */}
      <section className="pt-10">
        <div className="flex justify-between items-end mb-4">
          <div>
            <h2 className="text-slate-900 text-xs font-bold uppercase tracking-wider text-slate-500">
              Planes Disponibles
            </h2>
            <p className="text-slate-600 text-sm mt-0.5">
              Elige tu plan y reporta tu Pago Móvil a la tasa oficial del Banco Central de Venezuela (BCV).
            </p>
          </div>
          {pagoMovilInfo && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-700 bg-white px-3 py-1.5 rounded-xl font-medium border border-slate-200 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Tasa oficial BCV: <strong>Bs. {pagoMovilInfo.bcvRate.toFixed(2)}</strong> / USD
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.filter((p: PlanItem) => p.code !== 'trial' && p.code !== 'empresa').map((p: PlanItem) => {
            const launchOffer = p.features?.launchOffer
            const regularUsd = launchOffer?.regularPriceCents ? launchOffer.regularPriceCents / 100 : null
            const isCurrent = planCode === p.code
            const isPlanLoading = changePlanMutation.isPending && selectedPlanCode === p.code
            const priceUsd = p.priceCents / 100
            const priceBs = Math.round(priceUsd * bcvRate * 100) / 100

            return (
              <div
                key={p.id}
                className={`rounded-2xl border p-5 flex flex-col justify-between transition-all ${
                  isCurrent
                    ? 'border-[#075f55] bg-emerald-50/30 shadow-md ring-2 ring-[#075f55]/20'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <div>
                  {/* Badge */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-black uppercase text-slate-400 tracking-wider">
                      {p.name}
                    </span>
                    {p.features?.badge && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                        {p.features.badge}
                      </span>
                    )}
                  </div>

                  {/* Precio */}
                  <div className="mb-2">
                    {launchOffer && regularUsd !== null && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs font-semibold text-slate-400 line-through">
                          {`$${Number.isInteger(regularUsd) ? regularUsd : regularUsd.toFixed(2)}/mes`}
                        </span>
                        <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                          {`${launchOffer.discountPercent}% OFF · ${launchOffer.months} meses`}
                        </span>
                      </div>
                    )}
                    <span className="text-3xl font-black text-slate-900">
                      {p.priceCents === 0 ? 'Gratis' : `$${Number.isInteger(priceUsd) ? priceUsd : priceUsd.toFixed(2)}`}
                    </span>
                    {p.priceCents > 0 && <span className="text-xs text-slate-500 font-medium"> / mes</span>}
                  </div>

                  {/* Equivalente en Bolívares */}
                  {p.priceCents > 0 && (
                    <p className="text-[11px] font-semibold text-[#075f55] mb-4 bg-emerald-50/60 px-2 py-1 rounded-lg border border-emerald-100">
                      ≈ Bs. {priceBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })} (Tasa BCV)
                    </p>
                  )}
                  {p.priceCents === 0 && (
                    <p className="text-[11px] text-slate-400 mb-4 py-1">
                      7 días de prueba incluida
                    </p>
                  )}

                  <p className="text-xs text-slate-600 mb-4 min-h-[36px]">
                    {p.features?.description || `Incluye ${p.creditsIncluded.toLocaleString()} créditos IA.`}
                  </p>

                  {/* Beneficios */}
                  <ul className="space-y-2 mb-6 text-xs text-slate-700 border-t border-slate-100 pt-4">
                    <li className="flex items-center gap-2">
                      <Zap className="w-3.5 h-3.5 text-[#075f55] flex-shrink-0" />
                      <strong className="text-slate-900">{p.creditsIncluded.toLocaleString()}</strong> créditos IA
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      {p.features?.catalogLimit && p.features.catalogLimit > 0
                        ? `Hasta ${p.features.catalogLimit} repuestos/items`
                        : 'Catálogo ilimitado'}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      1 Conexión WhatsApp
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      Escalado automático a humano
                    </li>
                    {p.features?.receiptAnalysis && (
                      <li className="flex items-center gap-2 text-indigo-700 font-medium">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        Validación de pagos móviles
                      </li>
                    )}
                  </ul>
                </div>

                {/* Acciones */}
                <div className="space-y-2">
                  {p.priceCents > 0 ? (
                    <button
                      onClick={() => openPaymentModal(p)}
                      className="w-full py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 bg-[#075f55] hover:bg-[#064e46] text-white shadow-sm hover:shadow active:scale-[0.98]"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      Reportar Pago Móvil
                    </button>
                  ) : (
                    <button
                      disabled={isCurrent || isPlanLoading}
                      onClick={() => handleSelectPlan(p.code)}
                      className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        isCurrent
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                          : 'bg-slate-800 hover:bg-slate-900 text-white shadow-sm'
                      }`}
                    >
                      {isCurrent ? 'Plan actual' : `Elegir ${p.name}`}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-900 text-white p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-300 shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-white">Plan Empresa (A medida)</h3>
              <p className="text-xs text-slate-300 mt-0.5">¿Necesitas múltiples sucursales, mayor volumen de mensajes o integraciones personalizadas? Contáctanos para conversar sobre tu plan empresarial.</p>
            </div>
          </div>
          <a href={getWhatsAppSalesUrl('Hola IridiaChat, deseo conversar sobre el Plan Empresa para mi negocio.')} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto shrink-0 px-4 py-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-900 text-xs font-bold transition-colors text-center">Contactar por Plan Empresa</a>
        </div>
      </section>

      {/* ── HISTORIAL DE PAGOS REPORTADOS ── */}
      {myReports.length > 0 && (
        <section className="pt-10">
          <h2 className="text-slate-900 text-xs font-bold uppercase tracking-wider mb-3 text-slate-500">
            Mis Pagos Reportados
          </h2>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase">
                  <tr>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Referencia</th>
                    <th className="px-4 py-3">Monto Bs. (Tasa BCV)</th>
                    <th className="px-4 py-3">Comprobante</th>
                    <th className="px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {myReports.map((r: PaymentReportItem) => {
                    const statusConfig = {
                      pending: { bg: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pendiente de Validación' },
                      approved: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Aprobado y Activo' },
                      rejected: { bg: 'bg-red-100 text-red-800 border-red-200', label: 'Rechazado' },
                    }[r.status] || { bg: 'bg-slate-100 text-slate-700 border-slate-200', label: r.status }

                    return (
                      <tr key={r.id} className="hover:bg-slate-50/60">
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 font-bold text-slate-900">
                          {r.plan?.name || 'Plan'}
                        </td>
                        <td className="px-4 py-3 font-mono font-bold text-slate-800">
                          {r.referenceNumber}
                        </td>
                        <td className="px-4 py-3 text-slate-800 font-semibold">
                          Bs. {r.amountBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          <span className="text-[10px] text-slate-400 block">(${r.amountUsd} USD · Tasa {r.exchangeRate})</span>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={r.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#075f55] hover:underline font-semibold"
                          >
                            <FileText className="w-3.5 h-3.5" /> Ver capture
                          </a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${statusConfig.bg}`}>
                            {statusConfig.label}
                          </span>
                          {r.status === 'rejected' && r.rejectionReason && (
                            <p className="text-[10px] text-red-600 mt-0.5">Motivo: {r.rejectionReason}</p>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ── MÉTODOS DE PAGO Y SOPORTE LOCAL ── */}
      <section className="pt-10">
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border border-slate-200 text-[#075f55] shadow-sm flex-shrink-0">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-slate-900">Métodos de Pago Nacionales e Internacionales</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Por ahora procesamos pagos en bolívares vía <strong>Pago Móvil y transferencia bancaria a tasa oficial BCV</strong>.
                Más adelante habilitaremos PayPal, tarjeta de débito y Binance Pay.
              </p>
            </div>
          </div>
          <a
            href={getWhatsAppSalesUrl('Hola IridiaChat, deseo asistencia para pagar mi plan de suscripción.')}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 font-bold text-xs rounded-xl shadow-sm transition-colors whitespace-nowrap"
          >
            Contactar Asesor de Pagos
          </a>
        </div>
      </section>

      {/* ── MODAL: REPORTAR PAGO MÓVIL ── */}
      {reportingPlan && pagoMovilInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Cabecera */}
            <div className="bg-gradient-to-r from-[#075f55] to-[#0a7b6f] text-white p-5 flex justify-between items-center">
              <div>
                <h3 className="font-black text-base flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Reportar Pago Móvil
                </h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  Plan: <strong>{reportingPlan.name}</strong> (${reportingAmountUsd} USD)
                </p>
              </div>
              <button
                onClick={closePaymentModal}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitReport} className="p-6 space-y-5">
              {/* Box 1: Datos de Pago Móvil de la Empresa */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-2">
                <p className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">
                  Datos para realizar el Pago Móvil:
                </p>
                <div className="grid grid-cols-2 gap-2 text-slate-800">
                  <div>
                    <span className="text-slate-400 block text-[10px]">Banco:</span>
                    <strong className="text-slate-900">{pagoMovilInfo.bankName}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Cédula / RIF:</span>
                    <div className="flex items-center gap-1">
                      <strong>{pagoMovilInfo.idNumber}</strong>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(pagoMovilInfo.idNumber, 'Cédula/RIF')}
                        className="text-slate-400 hover:text-slate-700"
                        title="Copiar"
                      >
                        {copiedField === 'Cédula/RIF' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Teléfono:</span>
                    <div className="flex items-center gap-1">
                      <strong>{pagoMovilInfo.phoneNumber}</strong>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(pagoMovilInfo.phoneNumber, 'Teléfono')}
                        className="text-slate-400 hover:text-slate-700"
                        title="Copiar"
                      >
                        {copiedField === 'Teléfono' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Beneficiario:</span>
                    <strong>{pagoMovilInfo.accountHolder}</strong>
                  </div>
                </div>
              </div>

              {/* Box 2: Monto a transferir a Tasa BCV */}
              <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider block">
                    Monto exacto a transferir (Tasa BCV {bcvRate.toFixed(2)})
                  </span>
                  <span className="text-xl font-black text-slate-900">
                    Bs. {reportingAmountBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs text-slate-500 block">Equivalente a ${reportingAmountUsd} USD</span>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(reportingAmountBs.toFixed(2), 'Monto en Bs')}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm"
                >
                  {copiedField === 'Monto en Bs' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  Copiar Bs
                </button>
              </div>

              {/* Box 3: Formulario de comprobante */}
              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">
                    Número de Referencia <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: 12345678"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-slate-800 font-mono focus:ring-2 focus:ring-[#075f55] outline-none"
                  />
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">
                    Banco Emisor (Desde dónde transferiste)
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Banesco, Mercantil, Venezuela..."
                    value={issuingBank}
                    onChange={(e) => setIssuingBank(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-[#075f55] outline-none"
                  />
                </div>

                <div>
                  <label className="font-bold text-slate-700 block mb-1">
                    Captura del Comprobante de Pago <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-slate-300 hover:border-[#075f55] rounded-xl p-4 text-center cursor-pointer transition-colors bg-slate-50 relative">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      required
                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    {receiptPreview ? (
                      <div className="flex flex-col items-center">
                        <img src={receiptPreview} alt="Comprobante" className="max-h-36 rounded-lg object-contain mb-2 shadow-sm border border-slate-200" />
                        <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> Comprobante seleccionado ({receiptFile?.name})
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-2 text-slate-500">
                        <Upload className="w-8 h-8 text-slate-400 mb-1" />
                        <span className="font-semibold text-xs text-slate-700">Haz clic para subir la captura</span>
                        <span className="text-[10px] text-slate-400">PNG, JPG o WEBP hasta 10MB</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-600 block mb-1">
                    Observaciones o Notas (opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Pago realizado desde cuenta de titular secundario"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-slate-800 focus:ring-2 focus:ring-[#075f55] outline-none"
                  />
                </div>
              </div>

              {/* Box 4: Banner informativo sobre pasarela futura */}
              <div className="bg-slate-100 border border-slate-200 rounded-xl p-3 flex items-start gap-2.5 text-slate-600 text-[11px]">
                <CreditCard className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Próximamente:</strong> Habilitaremos pagos con PayPal, tarjeta de débito y Binance Pay.
                  Por ahora validamos tu Pago Móvil o transferencia bancaria manualmente en minutos.
                </p>
              </div>

              {/* Botón de Enviar */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="w-1/3 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitReportMutation.isPending}
                  className="w-2/3 py-2.5 px-4 bg-[#075f55] hover:bg-[#064e46] text-white font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  {submitReportMutation.isPending && (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {submitReportMutation.isPending ? 'Enviando comprobante...' : 'Confirmar y Enviar Reporte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
